import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

import { Dropdown } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV, faEye, faCheck, faTimes, faFilter, faPlus, faLink, faUnlink, faMapMarkerAlt, faHistory } from "@fortawesome/free-solid-svg-icons";

import { DashboardLayout } from "../../Components/Layout/DashboardLayout";
import CustomTable from "./../../Components/CustomTable";
import CustomModal from "../../Components/CustomModal";
import CustomPagination from "../../Components/CustomPagination"
import CustomInput from "../../Components/CustomInput";
import CustomButton from "../../Components/CustomButton";
import Loader from '../../Components/Loader';

import "./style.css";

import { useGetFamilyLinksQuery, useUpdateFamilyLinkMutation } from "../../Redux/Apis/Family";
import { dateFormatter } from "../../Utils";
import { placeholderImage } from "../../Assets/images";

const statusValues = [
  { text: "All", value: "" },
  { text: "Active", value: "active" },
  { text: "Pending", value: "pending" },
  { text: "Revoked", value: "revoked" },
];

const perPageValues = [
  { text: "10", value: 10 },
  { text: "20", value: 20 },
  { text: "50", value: 50 }
];

export const FamilyManagement = () => {
  const [updateFamilyLink, { isLoading: isUpdating }] = useUpdateFamilyLinkMutation();

  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedLink, setSelectedLink] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(perPageValues[0].value);
  const [inputValue, setInputValue] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const { data, isLoading, isFetching, refetch } = useGetFamilyLinksQuery({
    page: currentPage,
    limit: itemsPerPage,
    search: inputValue,
    status: statusFilter,
  });

  const toggleFilter = () => setIsFilterOpen(!isFilterOpen);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleRevoke = async () => {
    if (!selectedLink) return;
    await updateFamilyLink({ id: selectedLink._id, status: 'revoked' });
    refetch();
    setSelectedLink(null);
    setShowRevokeModal(false);
    setSuccessMessage('Connection has been revoked');
    setShowSuccessModal(true);
  };

  const handleActivate = async () => {
    if (!selectedLink) return;
    await updateFamilyLink({ id: selectedLink._id, status: 'active' });
    refetch();
    setSelectedLink(null);
    setShowActivateModal(false);
    setSuccessMessage('Connection has been activated');
    setShowSuccessModal(true);
  };

  const handleChange = (e) => {
    setInputValue(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  useEffect(() => {
    document.title = 'Landing List | Marshall';
  }, []);

  const getStatusBadge = (status) => {
    const statusClasses = {
      active: 'badge-success',
      pending: 'badge-warning',
      revoked: 'badge-danger',
    };
    return (
      <span className={`badge ${statusClasses[status] || 'badge-secondary'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const tableHeaders = [
    { key: "id", title: "S.No" },
    { key: "parent", title: "Guardian" },
    { key: "child", title: "Member" },
    { key: "status", title: "Status" },
    { key: "linkedBy", title: "Connected Via" },
    { key: "linkedAt", title: "Connected Date" },
    { key: "actions", title: "Actions" },
  ];

  return (
    <>
      <DashboardLayout>
        <div className="container-fluid">
          <div className="row mb-3">
            <div className="col-12">
              <div className="dashCard">
                <div className="row mb-3 justify-content-between">
                  <div className="col-md-6 mb-2">
                    <h2 className="mainTitle">Marshall Connections</h2>
                  </div>
                  <div className="col-md-6 mb-2">
                    <div className="addUser">
                      <Link to="/family-management/create" className="primaryButton">
                        <FontAwesomeIcon icon={faPlus} className="me-2" />
                        Create Connection
                      </Link>
                      <CustomButton type="button" icon={faFilter} className="primaryButton rounded-50" onClick={toggleFilter} />
                      <CustomInput type="text" placeholder="Search by name or email..." value={inputValue} inputClass="mainInput" onChange={handleChange} />
                    </div>
                  </div>
                </div>
                
                {/* Status Filter Pills */}
                <div className="row mb-3">
                  <div className="col-12">
                    <div className="statusFilters">
                      {statusValues.map((status) => (
                        <button
                          key={status.value}
                          className={`statusPill ${statusFilter === status.value ? 'active' : ''}`}
                          onClick={() => handleStatusFilterChange(status.value)}
                        >
                          {status.text}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="row mb-3">
                  {isLoading ? <Loader /> :
                    <div className="col-12">
                      <CustomTable
                        headers={tableHeaders}
                        isFilterOpen={isFilterOpen}
                        dateFilter={false}
                        perPage={true}
                        perPageValues={perPageValues}
                        itemsPerPage={itemsPerPage}
                        setItemsPerPage={setItemsPerPage}
                        length={data?.data?.length}
                      >
                        <tbody>
                          {data?.data?.map((item, index) => (
                            <tr key={item._id}>
                              <td>{(index + 1) + ((currentPage - 1) * itemsPerPage)}</td>
                              <td className="text-capitalize">
                                <div className="userCell">
                                  <img
                                    src={item.parentId?.picture ? `${process.env.REACT_APP_IMAGE_ENDPOINT}${item.parentId?.picture}` : placeholderImage}
                                    alt="parent"
                                    className="thumbnail"
                                  />
                                  <div>
                                    <div className="userName">{item.parentId?.name || 'Unknown'}</div>
                                    <div className="userEmail">{item.parentId?.email}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="text-capitalize">
                                {item.childId ? (
                                  <div className="userCell">
                                    <img
                                      src={item.childId?.picture ? `${process.env.REACT_APP_IMAGE_ENDPOINT}${item.childId?.picture}` : placeholderImage}
                                      alt="child"
                                      className="thumbnail"
                                    />
                                    <div>
                                      <div className="userName">{item.childId?.name || 'Unknown'}</div>
                                      <div className="userEmail">{item.childId?.email}</div>
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-muted">Pending scan...</span>
                                )}
                              </td>
                              <td>{getStatusBadge(item.status)}</td>
                              <td>
                                <span className={`linkedByBadge ${item.linkedBy === 'admin' ? 'admin' : 'qr'}`}>
                                  {item.linkedBy === 'admin' ? 'Admin' : 'QR Scan'}
                                </span>
                              </td>
                              <td>{item.linkedAt ? dateFormatter(item.linkedAt) : '-'}</td>
                              <td>
                                <Dropdown className="tableDropdown">
                                  <Dropdown.Toggle variant="transparent" className="notButton classicToggle">
                                    <FontAwesomeIcon icon={faEllipsisV} />
                                  </Dropdown.Toggle>
                                  <Dropdown.Menu align="end" className="tableDropdownMenu">
                                    <Link 
                                      to={`/family-management/details/${item._id}`} 
                                      className="tableAction"
                                    >
                                      <FontAwesomeIcon icon={faEye} className="tableActionIcon" />
                                      View Details
                                    </Link>
                                    {item.childId && item.status === 'active' && (
                                      <Link 
                                        to={`/family-management/location/${item._id}`} 
                                        className="tableAction"
                                      >
                                        <FontAwesomeIcon icon={faMapMarkerAlt} className="tableActionIcon" />
                                        Location History
                                      </Link>
                                    )}
                                    {item.status === 'active' && (
                                      <button 
                                        className="tableAction text-danger"
                                        onClick={() => {
                                          setSelectedLink(item);
                                          setShowRevokeModal(true);
                                        }}
                                      >
                                        <FontAwesomeIcon icon={faUnlink} className="tableActionIcon" />
                                        Revoke
                                      </button>
                                    )}
                                    {item.status === 'revoked' && (
                                      <button 
                                        className="tableAction text-success"
                                        onClick={() => {
                                          setSelectedLink(item);
                                          setShowActivateModal(true);
                                        }}
                                      >
                                        <FontAwesomeIcon icon={faLink} className="tableActionIcon" />
                                        Restore
                                      </button>
                                    )}
                                  </Dropdown.Menu>
                                </Dropdown>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </CustomTable>
                      {data?.data?.length === 0 && (
                        <div className="emptyState">
                          <FontAwesomeIcon icon={faLink} className="emptyIcon" />
                          <h4>No Marshall Connections</h4>
                          <p>Create a new connection to enable Marshall protection between family accounts.</p>
                          <Link to="/family-management/create" className="primaryButton">
                            <FontAwesomeIcon icon={faPlus} className="me-2" />
                            Create Connection
                          </Link>
                        </div>
                      )}
                      <CustomPagination
                        length={data?.data?.length}
                        itemsPerPage={itemsPerPage}
                        totalItems={data?.pagination?.total || 0}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                      />
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>

          {/* Modals */}
          <CustomModal 
            loading={isUpdating} 
            show={showRevokeModal} 
            close={() => setShowRevokeModal(false)} 
            action={handleRevoke} 
            heading='Are you sure you want to revoke this Marshall connection?' 
          />
          
          <CustomModal 
            loading={isUpdating} 
            show={showActivateModal} 
            close={() => setShowActivateModal(false)} 
            action={handleActivate} 
            heading='Are you sure you want to restore this Marshall connection?' 
          />
          
          <CustomModal 
            show={showSuccessModal} 
            close={() => setShowSuccessModal(false)} 
            success 
            heading={successMessage} 
          />
        </div>
      </DashboardLayout>
    </>
  );
};

export default FamilyManagement;

